import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Search, Plus, Minus, Trash2, Truck } from 'lucide-react';
import { ordersApi } from '../../api/endpoints/orders';
import { productsApi } from '../../api/endpoints/products';
import { shippingApi } from '../../api/endpoints/shipping';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Toggle } from '../../components/ui/Toggle';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { CheckboxGroup } from '../../components/ui/CheckboxGroup';
import { useToast } from '../../components/ui/Alert';
import { useStore } from '../../contexts/StoreContext';
import { useDebounce } from '../../hooks/useDebounce';
import { getCdnUrl, formatCurrency, torodLabel, torodId } from '../../lib/formatters';
import { buildInputEntry, isMultiSelectField, isValueFilled, toValueArray, type FieldValue } from '../../lib/productFieldValues';
import type { Product, CustomField } from '../../types/product';

interface LineItem {
  productId: string;
  title: string;
  price: number;
  quantity: number;
  image?: string;
  productType?: string;
  fields?: CustomField[];
  /** multiSelect fields hold the array of ticked option names. */
  fieldValues?: Record<string, FieldValue>;
}

const PHONE_COUNTRY_CODES = [
  // Gulf
  { value: '966', label: '+966 🇸🇦' },
  { value: '971', label: '+971 🇦🇪' },
  { value: '973', label: '+973 🇧🇭' },
  { value: '965', label: '+965 🇰🇼' },
  { value: '974', label: '+974 🇶🇦' },
  { value: '968', label: '+968 🇴🇲' },
  { value: '967', label: '+967 🇾🇪' },
  { value: '964', label: '+964 🇮🇶' },
  // Arab
  { value: '20', label: '+20 🇪🇬' },
  { value: '962', label: '+962 🇯🇴' },
  { value: '961', label: '+961 🇱🇧' },
  { value: '963', label: '+963 🇸🇾' },
  { value: '970', label: '+970 🇵🇸' },
  { value: '218', label: '+218 🇱🇾' },
  { value: '216', label: '+216 🇹🇳' },
  { value: '213', label: '+213 🇩🇿' },
  { value: '212', label: '+212 🇲🇦' },
  { value: '249', label: '+249 🇸🇩' },
  { value: '252', label: '+252 🇸🇴' },
  { value: '253', label: '+253 🇩🇯' },
  { value: '222', label: '+222 🇲🇷' },
  { value: '269', label: '+269 🇰🇲' },
  // International
  { value: '1', label: '+1 🇺🇸' },
  { value: '44', label: '+44 🇬🇧' },
  { value: '33', label: '+33 🇫🇷' },
  { value: '49', label: '+49 🇩🇪' },
  { value: '90', label: '+90 🇹🇷' },
  { value: '92', label: '+92 🇵🇰' },
  { value: '91', label: '+91 🇮🇳' },
  { value: '60', label: '+60 🇲🇾' },
  { value: '62', label: '+62 🇮🇩' },
  { value: '234', label: '+234 🇳🇬' },
  { value: '27', label: '+27 🇿🇦' },
  { value: '55', label: '+55 🇧🇷' },
  { value: '86', label: '+86 🇨🇳' },
];

export function OrderCreate() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();
  const currency = currentStore?.currency ?? 'SAR';

  // Line items
  const [items, setItems] = useState<LineItem[]>([]);

  // Product search
  const [productSearch, setProductSearch] = useState('');
  const debouncedSearch = useDebounce(productSearch, 300);

  // Customer
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('966');

  // Shipping - Torod location selectors
  const [shippingStreet, setShippingStreet] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingPrice, setShippingPrice] = useState(0);
  const [cod, setCod] = useState(false);
  const [torodCountry, setTorodCountry] = useState<number | null>(null);
  const [torodRegion, setTorodRegion] = useState<number | null>(null);
  const [torodCity, setTorodCity] = useState<number | null>(null);
  const [torodDistrict, setTorodDistrict] = useState<number | null>(null);
  const [shippingCityName, setShippingCityName] = useState('');
  const [shippingCountryName, setShippingCountryName] = useState('');

  // Other
  const [couponId, setCouponId] = useState('');
  const [notes, setNotes] = useState('');

  // Validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load all products for the browse grid
  const { data: allProducts } = useQuery({
    queryKey: ['products-all', storeId],
    queryFn: () => productsApi.list(storeId!, { limit: 100 }),
    enabled: !!storeId,
  });

  // Filtered product list: search narrows the grid
  const displayProducts = (allProducts?.products ?? []).filter(p => {
    if (!debouncedSearch || debouncedSearch.length < 2) return true;
    const q = debouncedSearch.toLowerCase();
    return p.title.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q);
  });

  // Torod location queries
  const { data: torodCountries } = useQuery({
    queryKey: ['torod-countries', storeId],
    queryFn: () => shippingApi.getTorodCountries(storeId!),
    enabled: !!storeId,
  });
  const { data: torodRegions } = useQuery({
    queryKey: ['torod-regions', storeId, torodCountry],
    queryFn: () => shippingApi.getTorodRegions(storeId!, torodCountry!),
    enabled: !!storeId && torodCountry !== null,
  });
  const { data: torodCities } = useQuery({
    queryKey: ['torod-cities', storeId, torodRegion],
    queryFn: () => shippingApi.getTorodCities(storeId!, torodRegion!),
    enabled: !!storeId && torodRegion !== null,
  });
  const { data: torodDistricts } = useQuery({
    queryKey: ['torod-districts', storeId, torodCity],
    queryFn: () => shippingApi.getTorodDistricts(storeId!, torodCity!),
    enabled: !!storeId && torodCity !== null,
  });

  // Auto-select Saudi Arabia when countries load
  useEffect(() => {
    if (torodCountries?.length && torodCountry === null) {
      const sa = torodCountries.find(c => c.country_name?.toLowerCase() === 'saudi arabia' || c.country_name_ar === 'السعودية');
      if (sa) {
        setTorodCountry(torodId(sa, 'country'));
        setShippingCountryName(torodLabel(sa, i18n.language));
      }
    }
  }, [torodCountries, torodCountry, i18n.language]);

  const addProduct = useCallback((product: Product) => {
    setItems(prev => {
      const existing = prev.find(i => i.productId === product._id);
      if (existing) {
        return prev.map(i =>
          i.productId === product._id ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }
      return [...prev, {
        productId: product._id,
        title: product.title,
        price: product.price,
        quantity: 1,
        image: product.images?.[0],
        productType: product.productType,
        fields: product.fields,
        fieldValues: product.fields?.length
          ? Object.fromEntries(product.fields.map(f => [String(f.id), '']))
          : undefined,
      }];
    });
    setProductSearch('');
  }, []);

  const updateQty = (productId: string, delta: number) => {
    setItems(prev => prev.map(i => {
      if (i.productId !== productId) return i;
      const newQty = Math.max(1, i.quantity + delta);
      return { ...i, quantity: newQty };
    }));
  };

  const setQty = (productId: string, qty: number) => {
    setItems(prev => prev.map(i => {
      if (i.productId !== productId) return i;
      return { ...i, quantity: Math.max(1, qty) };
    }));
  };

  const removeItem = (productId: string) => {
    setItems(prev => prev.filter(i => i.productId !== productId));
  };

  const updateFieldValue = (productId: string, fieldId: string, value: FieldValue) => {
    setItems(prev => prev.map(i => {
      if (i.productId !== productId) return i;
      return { ...i, fieldValues: { ...i.fieldValues, [fieldId]: value } };
    }));
  };

  // Determine if any item requires shipping
  const hasPhysicalProduct = items.some(i => !i.productType || i.productType === 'physical');

  // Auto-fill shipping phone from customer info when a physical product is first added
  const [shippingAutoFilled, setShippingAutoFilled] = useState(false);
  useEffect(() => {
    if (hasPhysicalProduct && !shippingAutoFilled) {
      if (customerName && !shippingPhone) setShippingPhone(customerPhone);
      setShippingAutoFilled(true);
    }
    if (!hasPhysicalProduct) setShippingAutoFilled(false);
  }, [hasPhysicalProduct]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const total = subtotal + (hasPhysicalProduct ? shippingPrice : 0);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (items.length === 0) {
      errors.items = 'يرجى إضافة منتج واحد على الأقل';
    }
    for (const item of items) {
      if (item.fields) {
        for (const field of item.fields) {
          if (field.requirement === 'required' && !isValueFilled(item.fieldValues?.[String(field.id)])) {
            errors[`field_${item.productId}_${field.id}`] = `${field.title} مطلوب`;
          }
        }
      }
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      const el = document.querySelector(`[data-error-key="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    createMutation.mutate();
  };

  const getErrorMessage = (err: unknown): string => {
    const msg = (err as any)?.response?.data?.message || (err as any)?.message || '';
    if (msg.includes('quantity') || msg.includes('Quantity')) return 'كمية المنتج نفذت، تأكد من كميات المنتجات بصفحة المنتجات';
    if (msg.includes('Code reservation')) return 'فشل حجز الأكواد، تأكد من توفر أكواد كافية للمنتج';
    if (msg.includes('Items are required')) return 'يرجى إضافة منتج واحد على الأقل';
    return msg || 'حدث خطأ في إنشاء الطلب، يرجى المحاولة مرة أخرى';
  };

  const createMutation = useMutation({
    mutationFn: () =>
      ordersApi.create(storeId!, {
        items: items.map(i => ({
          productId: i.productId,
          quantity: i.quantity,
          // Backend (formatItems.js) validates each Input against the product field
          // definition: it expects Inputs[fieldId] = { value, title, fieldType }, not a
          // raw value. Sending the flat map yields "product input invalid". Keep every
          // field key (even empty ones) so optional fields don't trip "product field
          // missing" once any field is filled.
          ...(i.fieldValues && Object.values(i.fieldValues).some(v => isValueFilled(v))
            ? {
                Inputs: Object.fromEntries(
                  Object.entries(i.fieldValues).map(([fieldId, value]) => [
                    fieldId,
                    buildInputEntry(i.fields?.find(f => String(f.id) === fieldId), fieldId, value),
                  ]),
                ),
              }
            : {}),
        })),
        clientInfo: {
          name: customerName || undefined,
          email: customerEmail || undefined,
          phoneNumber: customerPhone?.replace(/\D/g, '') || undefined,
          phoneCountryCode: phoneCountryCode || '966',
        },
        address: hasPhysicalProduct && (shippingStreet || torodCity) ? {
          street: shippingStreet,
          city: shippingCityName || String(torodCity ?? ''),
          country: shippingCountryName || String(torodCountry ?? ''),
          district: torodDistrict ? String(torodDistrict) : undefined,
          phone: shippingPhone || undefined,
        } as any : undefined,
        shippingPrice: hasPhysicalProduct && shippingPrice ? shippingPrice : undefined,
        coupon: couponId || undefined,
        cod: hasPhysicalProduct && cod ? true : undefined,
        notes: notes || undefined,
      } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('orders.createSuccess'));
      navigate(`/${storeId}/orders`);
    },
    onError: (err) => toast('error', getErrorMessage(err)),
  });

  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/orders`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold">{t('orders.addOrder')}</h1>
      </div>

      <div className="space-y-6">
        {/* ── Products ── */}
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
          <h2 className="text-base font-semibold mb-4">{t('orders.items')}</h2>

          {/* Search bar */}
          <div className="mb-4">
            <div className="flex items-center gap-2 rounded-lg border border-border-default bg-bg-field px-3 py-2">
              <Search className="h-4 w-4 text-text-muted shrink-0" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder={t('orderCreate.searchProducts', 'Search products to add...')}
                className="flex-1 bg-transparent text-sm outline-none"
              />
            </div>
          </div>

          {/* Product browse grid */}
          {displayProducts.length > 0 ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(90px,1fr))] gap-2 max-h-44 overflow-y-auto mb-4 p-1">
              {displayProducts.map((p) => (
                <button
                  key={p._id}
                  onClick={() => addProduct(p)}
                  className="flex flex-col items-center gap-1 rounded-lg border border-border-default/50 bg-bg-tertiary p-2 hover:border-accent hover:bg-accent/5 transition-colors text-center"
                >
                  {p.images?.[0] ? (
                    <img src={getCdnUrl(p.images[0], currentStore?._id)} alt="" className="h-12 w-12 rounded object-cover" />
                  ) : (
                    <div className="h-12 w-12 rounded bg-bg-hover" />
                  )}
                  <p className="text-xs font-medium truncate w-full">{p.title}</p>
                  <span className="text-xs text-accent">{formatCurrency(p.price, currency)}</span>
                </button>
              ))}
            </div>
          ) : allProducts ? (
            <p className="text-sm text-text-muted text-center py-3 mb-4">{t('orderCreate.noProducts', 'No products found.')}</p>
          ) : null}

          {/* Line items */}
          {formErrors.items && <p className="text-sm text-danger text-center" data-error-key="items">{formErrors.items}</p>}
          {items.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-6">{t('orderCreate.noItems', 'No items added yet. Search for products above.')}</p>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.productId} className="space-y-2">
                  <div
                    className="flex items-center gap-3 rounded-lg border border-border-default/50 bg-bg-tertiary p-3"
                  >
                    {item.image ? (
                      <img src={getCdnUrl(item.image, currentStore?._id)} alt="" className="h-10 w-10 rounded-lg object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-bg-hover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-text-muted">{formatCurrency(item.price, currency)}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => updateQty(item.productId, -1)}
                        disabled={item.quantity <= 1}
                        className="flex h-7 w-7 items-center justify-center rounded border border-border-default text-text-muted hover:bg-bg-hover disabled:opacity-30"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => setQty(item.productId, parseInt(e.target.value) || 1)}
                        className="w-10 text-center text-sm font-medium bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        onClick={() => updateQty(item.productId, 1)}
                        className="flex h-7 w-7 items-center justify-center rounded border border-border-default text-text-muted hover:bg-bg-hover"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <span className="text-sm font-medium w-20 text-end">
                      {formatCurrency(item.price * item.quantity, currency)}
                    </span>
                    <button
                      onClick={() => removeItem(item.productId)}
                      className="rounded-lg p-1.5 text-text-muted hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Product custom fields */}
                  {item.fields && item.fields.length > 0 && (
                    <div className="ms-12 grid gap-3 sm:grid-cols-2 rounded-lg border border-border-default/30 bg-bg-secondary p-3">
                      {item.fields.map((field) => {
                        const fieldKey = String(field.id);
                        const errorKey = `field_${item.productId}_${field.id}`;
                        const rawValue = item.fieldValues?.[fieldKey] ?? '';
                        const value = Array.isArray(rawValue) ? '' : rawValue;
                        const fieldError = formErrors[errorKey];

                        if (isMultiSelectField(field.fieldType) && field.options?.length) {
                          return (
                            <div key={fieldKey} className="sm:col-span-2" data-error-key={errorKey}>
                              <CheckboxGroup
                                label={field.title}
                                required={field.requirement === 'required'}
                                options={field.options.map((opt) => ({
                                  value: opt.name,
                                  label: opt.priceModifier
                                    ? `${opt.name} (${opt.priceModifier > 0 ? '+' : ''}${formatCurrency(opt.priceModifier, currency)})`
                                    : opt.name,
                                }))}
                                value={toValueArray(rawValue)}
                                onChange={(next) => { updateFieldValue(item.productId, fieldKey, next); setFormErrors(prev => { const n = { ...prev }; delete n[errorKey]; return n; }); }}
                                error={fieldError}
                              />
                            </div>
                          );
                        }

                        if (field.fieldType === 'bigText') {
                          return (
                            <div key={fieldKey} className="sm:col-span-2" data-error-key={errorKey}>
                              <Textarea
                                label={field.title}
                                required={field.requirement === 'required'}
                                value={value}
                                onChange={(e) => { updateFieldValue(item.productId, fieldKey, e.target.value); setFormErrors(prev => { const n = { ...prev }; delete n[errorKey]; return n; }); }}
                                rows={2}
                                className={fieldError ? 'border-danger' : ''}
                              />
                              {fieldError && <p className="text-xs text-danger mt-1">{fieldError}</p>}
                            </div>
                          );
                        }

                        if (field.fieldType === 'selection' && field.options?.length) {
                          return (
                            <div key={fieldKey} data-error-key={errorKey}>
                              <Select
                                label={field.title}
                                required={field.requirement === 'required'}
                                placeholder={t('common.select')}
                                options={field.options.map((opt) => ({
                                  value: opt.name,
                                  label: opt.priceModifier
                                    ? `${opt.name} (${opt.priceModifier > 0 ? '+' : ''}${formatCurrency(opt.priceModifier, currency)})`
                                    : opt.name,
                                }))}
                                value={value}
                                onChange={(e) => { updateFieldValue(item.productId, fieldKey, e.target.value); setFormErrors(prev => { const n = { ...prev }; delete n[errorKey]; return n; }); }}
                                error={fieldError}
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={fieldKey} data-error-key={errorKey}>
                            <Input
                              label={field.title}
                              required={field.requirement === 'required'}
                              type={field.fieldType === 'number' ? 'number' : field.fieldType === 'color' ? 'color' : 'text'}
                              value={value}
                              onChange={(e) => { updateFieldValue(item.productId, fieldKey, e.target.value); setFormErrors(prev => { const n = { ...prev }; delete n[errorKey]; return n; }); }}
                              error={fieldError}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}

              {/* Totals */}
              <div className="border-t border-border-default pt-3 space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">{t('orders.subtotal')}</span>
                  <CurrencyDisplay amount={subtotal} currency={currency} />
                </div>
                {shippingPrice > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">{t('orders.shipping')}</span>
                    <CurrencyDisplay amount={shippingPrice} currency={currency} />
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-1 border-t border-border-default">
                  <span>{t('common.total')}</span>
                  <CurrencyDisplay amount={total} currency={currency} />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── Customer ── */}
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <h2 className="text-base font-semibold mb-4">{t('orders.customer')}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={t('common.name')} value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
            <Input label={t('common.email')} type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-text-secondary">{t('common.phone')}</label>
            <div className="flex gap-2" dir="ltr">
              <div className="w-28 shrink-0">
                <Select
                  options={PHONE_COUNTRY_CODES}
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  dir="ltr"
                />
              </div>
              <div className="flex-1">
                <Input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  dir="ltr"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Shipping Address - only shown when physical products are in the order ── */}
        {hasPhysicalProduct && <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <h2 className="flex items-center gap-2 text-base font-semibold mb-4">
            <Truck className="h-5 w-5 text-accent" />
            {t('orders.shippingAddress')}
          </h2>
          {/* Torod cascading location selectors */}
          <div className="grid gap-4 sm:grid-cols-2">
            <SearchableSelect
              label={t('shipping.country')}
              placeholder={t('shipping.selectCountry')}
              options={(torodCountries ?? []).map(c => ({ value: torodId(c, 'country') ?? '', label: torodLabel(c, i18n.language) }))}
              value={torodCountry}
              onChange={(v, opt) => {
                const numV = v !== null ? Number(v) : null;
                setTorodCountry(numV);
                setTorodRegion(null); setTorodCity(null); setTorodDistrict(null);
                setShippingCountryName(opt?.label || '');
              }}
            />
            <SearchableSelect
              label={t('shipping.region')}
              placeholder={t('shipping.selectRegion')}
              options={(torodRegions ?? []).map(r => ({ value: torodId(r, 'region') ?? '', label: torodLabel(r, i18n.language) }))}
              value={torodRegion}
              disabled={!torodCountry}
              onChange={(v) => {
                const numV = v !== null ? Number(v) : null;
                setTorodRegion(numV); setTorodCity(null); setTorodDistrict(null);
              }}
            />
            <SearchableSelect
              label={t('shipping.city')}
              placeholder={t('shipping.selectCity')}
              options={(torodCities ?? []).map(c => ({ value: torodId(c, 'city') ?? '', label: torodLabel(c, i18n.language) }))}
              value={torodCity}
              disabled={!torodRegion}
              onChange={(v, opt) => {
                const numV = v !== null ? Number(v) : null;
                setTorodCity(numV); setTorodDistrict(null);
                setShippingCityName(opt?.label || '');
              }}
            />
            <SearchableSelect
              label={t('shipping.district')}
              placeholder={t('shipping.selectDistrict')}
              options={(torodDistricts ?? []).map(d => ({ value: torodId(d, 'district') ?? '', label: torodLabel(d, i18n.language) }))}
              value={torodDistrict}
              disabled={!torodCity}
              onChange={(v) => setTorodDistrict(v !== null ? Number(v) : null)}
            />
          </div>
          <Input label={t('shipping.address')} value={shippingStreet} onChange={(e) => setShippingStreet(e.target.value)} placeholder={t('orderCreate.streetPlaceholder', 'Street address, building, etc.')} />
          <Input label={t('common.phone')} value={shippingPhone} onChange={(e) => setShippingPhone(e.target.value)} />
          <Input
            label={t('orderCreate.shippingPrice', 'Shipping Price')}
            type="number"
            min="0"
            step="0.01"
            value={shippingPrice || ''}
            onChange={(e) => setShippingPrice(Number(e.target.value) || 0)}
          />
          <Toggle
            label={t('orderCreate.cod', 'Cash on Delivery (COD)')}
            checked={cod}
            onChange={setCod}
          />
        </section>}

        {/* ── Coupon & Notes ── */}
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <Input
            label={t('orderCreate.couponCode', 'Coupon Code')}
            value={couponId}
            onChange={(e) => setCouponId(e.target.value)}
            placeholder={t('orderCreate.couponPlaceholder', 'Enter coupon code (optional)')}
          />
          <Textarea
            id="notes"
            label={t('orders.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </section>

        {/* ── Actions ── */}
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={() => navigate(`/${storeId}/orders`)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={createMutation.isPending}
            disabled={items.length === 0}
          >
            {t('orders.addOrder')}
          </Button>
        </div>
      </div>
    </div>
  );
}
